import React, { useRef } from 'react'; // Import useRef
import { Modal, Descriptions, Collapse, Carousel } from 'antd'; // Import Carousel
import { fakeProImage } from '../../config/fakeData';
import { CaretLeftFilled, CaretRightFilled } from '@ant-design/icons';
import {ModalCarouselWrapper} from "./style";

const CustomPrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
        <CaretLeftFilled
            className={className}
            style={{
                ...style,
                fontSize: '40px',
                color: '#181C31',
                left: '-30px',
                zIndex: 1
            }}
            onClick={onClick}
        />
    );
};

const CustomNextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
        <CaretRightFilled
            className={className}
            style={{
                ...style,
                fontSize: '40px',
                color: '#181C31',
                right: '-5px',
                zIndex: 1,
            }}
            onClick={onClick}
        />
    );
};

const ProductDetailsModal = ({ open, onClose, data, currencySign }) => {
    const getAmount = (val) => {
        const amount = parseFloat(val || 0);
        return `${amount < 0 ? '-' : ''}${currencySign}${Math.abs(amount).toFixed(2)}`;
    };
    const getPercentage = (val) => `${parseFloat(val || 0).toFixed(2)}%`;

    const scrollContainerRef = useRef(null);
    const mainCollapseRef = useRef(null);

    const handleCollapseChange = (activeKeys) => {

        if (Array.isArray(activeKeys) && activeKeys.length > 0) {
            const newlyOpenedKey = activeKeys[activeKeys.length - 1];
            const panelHeader = mainCollapseRef.current?.querySelector(
                `.ant-collapse-item[data-key="${newlyOpenedKey}"] .ant-collapse-header`
            );

            if (panelHeader) {
                const closestScrollableParent = panelHeader.closest('div[style*="overflow-y: auto"]');

                if (closestScrollableParent) {
                    panelHeader.scrollIntoView({
                        behavior: 'smooth',
                        block: 'end', // Or 'nearest', 'start'
                        inline: 'nearest'
                    });
                } else {
                    panelHeader.scrollIntoView({
                        behavior: 'smooth',
                        block: 'end',
                        inline: 'nearest'
                    });
                }
            }
        }
    };


    const generateMetricsCollapseItems = (metricsData) => {
        if (!metricsData) return { plMetrics: [], performanceMetrics: [] };

        const revenueMetrics = [
            {
                key: '1',
                title: 'Total Sales',
                amount: getAmount(metricsData.more_data.sales.total),
                items: [
                    { label: 'Organic', value: getAmount(metricsData.more_data.sales.organic) },
                    { label: 'Sponsored product', value: getAmount(metricsData.more_data.sales.sp_sales) },
                    { label: 'Sponsored brand', value: getAmount(metricsData.more_data.sales.sb_sales) },
                    { label: 'Sponsored display', value: getAmount(metricsData.more_data.sales.sd_sales) }
                ]
            }
        ];

        const costMetrics = [
            {
                key: '1',
                title: 'Amazon Fees',
                amount: getAmount(metricsData.more_data.amazon_fees.total),
                items: Object.entries(metricsData.more_data.amazon_fees)
                    .filter(([key]) => key !== 'total')
                    .map(([key, value]) => ({
                        label: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
                        value: getAmount(value)
                    }))
            },
            {
                key: '2',
                title: 'Advertising Cost',
                amount: getAmount(metricsData.more_data.advertising_cost.total),
                items: [
                    { label: 'Sponsored product', value: getAmount(metricsData.more_data.advertising_cost.sp_cost) },
                    { label: 'Sponsored brand', value: getAmount(metricsData.more_data.advertising_cost.sb_cost) },
                    { label: 'Sponsored display', value: getAmount(metricsData.more_data.advertising_cost.sd_cost) }
                ]
            },
            {
                key: '3',
                title: 'Cost of Goods',
                amount: getAmount(metricsData.more_data.cost_of_goods.total),
                items: [
                    { label: 'COGS', value: getAmount(metricsData.more_data.cost_of_goods.cost_of_goods_sold) }
                ]
            },
            {
                key: '4',
                title: 'Expenses',
                amount: getAmount(metricsData.more_data.expenses.total),
                items: Object.entries(metricsData.more_data.expenses)
                    .filter(([key]) => key !== 'total')
                    .map(([key, value]) => ({
                        label: key, // Consider formatting key like amazon_fees for consistency
                        value: getAmount(value)
                    }))
            },
            {
                key: '5',
                title: 'Tax Liability',
                amount: getAmount(metricsData.tax),
                items: []
            }
        ];

        const profitMetrics = [
            {
                key: '1',
                title: 'Gross Profit',
                amount: getAmount(metricsData.more_data.gross_profit),
                items: []
            },
            {
                key: '2',
                title: 'Net Profit',
                amount: getAmount(metricsData.more_data.net_profit),
                items: []
            },
            {
                key: '3',
                title: 'Payments',
                amount: getAmount(metricsData.more_data.estimated_payout),
                items: []
            }
        ];

        const performanceMetrics = [
            {
                key: '1',
                title: 'Advertising clicks',
                amount: `${metricsData.more_data.advertising_clicks.total || 0}`, // Sum if total not available
                items: [
                    { label: 'Sponsored product', value: `${metricsData.more_data.advertising_clicks.sp_click || 0}` },
                    { label: 'Sponsored brand', value: `${metricsData.more_data.advertising_clicks.sb_click || 0}` },
                    { label: 'Sponsored display', value: `${metricsData.more_data.advertising_clicks.sd_click || 0}` },
                ]
            },
            {
                key: '2',
                title: 'Total units',
                amount: `${metricsData.more_data.units.total || 0}`, // Sum if total not available
                items: [
                    { label: 'Organic', value: `${metricsData.more_data.units.organic || 0}` },
                    { label: 'Sponsored product', value: `${metricsData.more_data.units.sp_units || 0}` },
                    { label: 'Sponsored brand', value: `${metricsData.more_data.units.sb_units || 0}` },
                    { label: 'Sponsored display', value: `${metricsData.more_data.units.sd_units || 0}` },
                ]
            },
            {
                key: '3',
                title: 'Ad conversion',
                amount: `${metricsData.more_data.advertising_clicks.total > 0
                    ? (
                        ((metricsData.more_data.units.sp_units + metricsData.more_data.units.sb_units + metricsData.more_data.units.sd_units || 0) / metricsData.more_data.advertising_clicks.total) * 100
                    ).toFixed(2)
                    : 0}%`,
                items: []
            },
            {
                key: '4',
                title: 'Refunds %',
                amount: `${metricsData.more_data.refunds_percent}%`,
                items: []
            }
        ];

        const formatSections = (items) =>
            items.map((section) => ({
                key: section.key,
                label: (
                    <div className="d-flex justify-content-between w-100">
                        <span className="text-gray-800 fw-bold text-hover-danger fs-6">
                            {section.title}
                        </span>
                        <span className="text-gray-800 fw-bolder fs-6 me-3 gotham_black">
                            {section.amount}
                        </span>
                    </div>
                ),
                children: section.items.length > 0 ? (
                    <>
                        {section.items.map((item, i) => (
                            <React.Fragment key={i}>
                                <div className="row g-4 mt-0 ps-9">
                                    <div className="col w-md-100 d-flex align-items-center justify-content-between pe-md-0 mt-1">
                                        <h3 className="mb-1 fw-semibold text-gray-700 fs-6 text-dark">
                                            {item.label}
                                        </h3>
                                        <h3 className="fs-6 text-gray-800 fw-bolder mb-0">
                                            {item.value}
                                        </h3>
                                    </div>
                                </div>
                                {i < section.items.length - 1 && (
                                    <div className="separator separator-dashed border-gray-300 my-2" />
                                )}
                            </React.Fragment>
                        ))}
                    </>
                ) : null,
                showArrow: section.items.length > 0,
                collapsible: section.items.length > 0 ? '' : 'disabled',
                className: section.items.length === 0 ? 'ant-collapse-item-no-child-padding' : ''
            }));

        return {
            revenueMetrics: formatSections(revenueMetrics),
            costMetrics: formatSections(costMetrics),
            profitMetrics: formatSections(profitMetrics),
            performanceMetrics: formatSections(performanceMetrics),
        };
    };

    const { revenueMetrics, costMetrics, profitMetrics, performanceMetrics } = generateMetricsCollapseItems(data);

    return (
            <ModalCarouselWrapper>
                <Modal
                    open={open}
                    onCancel={onClose}
                    footer={null}
                    width={700}
                    className="custom-modal"
                    centered
                >
                    {data && (
                        <div style={{
                            padding: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%'
                        }}> {/* Ensure this div takes full height */}
                            <div style={{textAlign: 'center', marginBottom: 20}}>
                                <img
                                    src={data?.image_url || fakeProImage}
                                    alt="Product"
                                    style={{
                                        maxHeight: 150,
                                        objectFit: 'contain',
                                        borderRadius: 12,
                                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
                                    }}
                                />
                            </div>

                            <h2 style={{textAlign: 'center', marginBottom: 24, color: '#1890ff'}}>
                                {data?.product_title}
                            </h2>
                            <h3 style={{textAlign: 'center', marginBottom: 24, color: '#595959'}}>
                                {data?.asin}
                            </h3>
                            <h3 style={{textAlign: 'center', marginBottom: 24, color: '#8c8c8c'}}>
                                {data?.sku}
                            </h3>
                            <Carousel
                                effect="scrollx"
                                className="metrics-modal-carousel"
                                arrows={true}
                                prevArrow={<CustomPrevArrow/>}
                                nextArrow={<CustomNextArrow/>}
                                dots={false}
                            >
                                {/* P&L Metrics Slide */}
                                <div>
                                    <div
                                        className="border-till border rounded p-3 px-4 mb-3"
                                        style={{borderColor: '#63CE94 !important', background: '#fff4e6'}}
                                    >
                                        <div className="d-flex align-items-center justify-content-center">
                                            <h5 className="mb-0 text-green fw-semibold fs-5">Revenue</h5>
                                        </div>
                                    </div>
                                    {/* Scrollable container for collapse within carousel slide */}
                                    <div className="modal-collapse-scrollable">
                                        <Collapse
                                            items={revenueMetrics}
                                            expandIcon={({isActive}) =>
                                                isActive ? (
                                                    <i className="ki-outline ki-down fs-3 text-gray-600"/>
                                                ) : (
                                                    <i className="ki-outline ki-right fs-3 text-gray-600"/>
                                                )
                                            }
                                            bordered={false}
                                            onChange={handleCollapseChange}
                                            ref={mainCollapseRef}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <div
                                        className="border-till border rounded p-3 px-4 mb-3"
                                        style={{borderColor: '#63CE94 !important', background: '#fff4e6'}}
                                    >
                                        <div className="d-flex align-items-center justify-content-center">
                                            <h5 className="mb-0 text-green fw-semibold fs-5">Cost</h5>
                                        </div>
                                    </div>
                                    {/* Scrollable container for collapse within carousel slide */}
                                    <div className="modal-collapse-scrollable">
                                        <Collapse
                                            items={costMetrics}
                                            expandIcon={({isActive}) =>
                                                isActive ? (
                                                    <i className="ki-outline ki-down fs-3 text-gray-600"/>
                                                ) : (
                                                    <i className="ki-outline ki-right fs-3 text-gray-600"/>
                                                )
                                            }
                                            bordered={false}
                                            onChange={handleCollapseChange} // Add onChange for auto-scrolling
                                            ref={mainCollapseRef} // Add ref for auto-scrolling
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div
                                        className="border-till border rounded p-3 px-4 mb-3"
                                        style={{borderColor: '#63CE94 !important', background: '#fff4e6'}}
                                    >
                                        <div className="d-flex align-items-center justify-content-center">
                                            <h5 className="mb-0 text-green fw-semibold fs-5">Profit</h5>
                                        </div>
                                    </div>
                                    {/* Scrollable container for collapse within carousel slide */}
                                    <div className="modal-collapse-scrollable">
                                        <Collapse
                                            items={profitMetrics}
                                            expandIcon={({isActive}) =>
                                                isActive ? (
                                                    <i className="ki-outline ki-down fs-3 text-gray-600"/>
                                                ) : (
                                                    <i className="ki-outline ki-right fs-3 text-gray-600"/>
                                                )
                                            }
                                            bordered={false}
                                            onChange={handleCollapseChange} // Add onChange for auto-scrolling
                                            ref={mainCollapseRef} // Add ref for auto-scrolling
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div
                                        className="border-till border rounded p-3 px-4 mb-3"
                                        style={{borderColor: '#63CE94 !important', background: '#fff4e6'}}
                                    >
                                        <div className="d-flex align-items-center justify-content-center">
                                            <h5 className="mb-0 text-green fw-semibold fs-5">Performance</h5>
                                        </div>
                                    </div>
                                    {/* Scrollable container for collapse within carousel slide */}
                                    <div className="modal-collapse-scrollable">
                                        <Collapse
                                            items={performanceMetrics}
                                            expandIcon={({isActive}) =>
                                                isActive ? (
                                                    <i className="ki-outline ki-down fs-3 text-gray-600"/>
                                                ) : (
                                                    <i className="ki-outline ki-right fs-3 text-gray-600"/>
                                                )
                                            }
                                            bordered={false}
                                            onChange={handleCollapseChange} // Add onChange for auto-scrolling
                                            ref={mainCollapseRef} // Add ref for auto-scrolling
                                        />
                                    </div>
                                </div>
                            </Carousel>

                            {/* Card Footer with Net Profit */}
                            <div style={{
                                padding: '12px 20px',
                                marginTop: '20px',
                                border: '1px dashed',
                                borderRadius: '8px',
                                borderColor: '#63CE94',
                                background: '#63ce9440',
                            }}>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <div style={{display: 'flex', alignItems: 'center'}}>
                                        <h5 style={{
                                            marginBottom: 0,
                                            color: '#0f7e3e',
                                            fontWeight: 600,
                                            marginLeft: '12px',
                                            fontSize: '1.5rem'
                                        }}>
                                            Net Profit
                                        </h5>
                                    </div>
                                    <div>
                                        <h4 style={{
                                            marginBottom: 0,
                                            fontWeight: 800,
                                            color: '#0f7e3e',
                                            fontSize: '2rem'
                                        }}> {/* fw-bolder text-green fs-3 */}
                                            {getAmount(data?.net_profit)}
                                        </h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal>
            </ModalCarouselWrapper>
    );
};

export default ProductDetailsModal;